import type { Data } from "@eai-toolkit/client";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import VpnKeyOutlined from "@mui/icons-material/VpnKeyOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { tracker } from "@openreplay/tracker";
import { useGetResourceMetadataQuery } from "@store/api/resourceApi";
import { useGetMeQuery } from "@store/api/usersApi";
import { useFilters } from "@store/hooks/useFilters";
import { useQueryStateFromUrl } from "@store/hooks/useQueryStateFromUrl";
import { useTableControls } from "@store/hooks/useTableControls";
import { useUpdateQueryState } from "@store/hooks/useUpdateQueryState";
import {
  defaultSortField,
  dehydrateDataQueryState,
  hydrateDataQueryState,
} from "@store/queryState";
import { fetchDataList } from "@store/slices/dataSlice";
import type { AppDispatch, RootState } from "@store/store";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  DEFAULT_CHIPS,
  LoadOverlay,
  QuickFilters,
  ViewWrapper,
} from "@components";
import { DataTable, useDataTableUrlInit } from "@components/DataTable";
import {
  NameIdSearch,
  type SearchFilter,
} from "@components/NameIdSearch/NameIdSearch";
import StarredFilterChip from "@components/StarredFilterChip/StarredFilterChip";
import { UserRoleSelector } from "@components/UserRoleSelector/UserRoleSelector";
import {
  useValidationContextValue,
  ValidationContext,
} from "@components/ValidationContext/ValidationContext";
import {
  FilterBar,
  NoneFoundMessage,
  SearchRow,
} from "@components/ViewWrapper/styles";

import {
  DATA_EXPLORER_PAGE_TITLE,
  getAccessRequestUrl,
  getDataDetailUrl,
  SEARCH_DEBOUNCE_MS,
} from "@helpers/constants";
import { DATA_EXPLORER_EVENTS } from "@helpers/events";

import type { RowAction } from "../../../data/types";
import { useDebounce } from "../../../hooks/useDebounce";
import { SHARED_BOOKMARK_CACHE_IDS } from "../../../hooks/useResourceBookmarks";
import { useRowNavigation } from "../../../hooks/useRowNavigation";
import { useTableStarring } from "../../../hooks/useTableStarring";
import { RESOURCE_NAME_FIELD } from "../../../store/api/resourceTypes";
import { DatePicker } from "../components/DatePicker";
import { useEditClassificationAction } from "../components/EditClassificationAction";
import { useExtractTarHelp } from "../components/ExtractTarDialog";
import fieldRenderers from "../data/fieldRenderers";
import type { FieldName } from "../data/fields";
import {
  defaultFieldLabels,
  fields,
  getDefaultSortOrder,
} from "../data/fields";
import {
  getFolderDownloadUrl,
  isFolderEmpty,
  ITEMS_PER_PAGE,
  startDownload,
} from "../helpers/api";

const QUICK_FILTERS = [
  { ...DEFAULT_CHIPS.isMine, label: "Created by me" },
  DEFAULT_CHIPS.isEditable,
  DEFAULT_CHIPS.isToday,
  DEFAULT_CHIPS.isWeek,
];

type QueryFieldName = FieldName | "endDate";

const getDataDetailPath = (data: Data) =>
  getDataDetailUrl(data.fullName || data.id);

// Each check is a listing request; checking a whole page at once would hold
// up the page's other requests behind them.
const MAX_CONCURRENT_CHECKS = 4;

/**
 * Which rows list anything (files or folders), so that empty data objects get
 * no download button. Rows found with entries keep them; empty ones are
 * checked again when the rows change, as data can gain files.
 */
export function useHasEntries(rows: Data[]) {
  const [hasEntries, setHasEntries] = useState<Record<string, boolean>>({});
  const withEntries = useRef(new Set<string>());
  const checking = useRef(new Set<string>());
  useEffect(() => {
    const queue = [...new Set(rows.map(({ id }) => id))].filter(
      (id) => !withEntries.current.has(id) && !checking.current.has(id),
    );
    let stopped = false;
    const work = async () => {
      while (!stopped) {
        const id = queue.shift();
        if (id === undefined) {
          return;
        }
        checking.current.add(id);
        // If the check fails, offer the download and let the server decide.
        const empty = await isFolderEmpty(id, "").catch(() => false);
        checking.current.delete(id);
        if (!empty) {
          withEntries.current.add(id);
        }
        setHasEntries((current) => ({ ...current, [id]: !empty }));
      }
    };
    for (let i = 0; i < MAX_CONCURRENT_CHECKS; i++) {
      void work();
    }
    return () => {
      stopped = true;
    };
  }, [rows]);
  return hasEntries;
}

const Home = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { list, loading, error, ready } = useSelector(
    (state: RootState) => state.data,
  );

  const queryState = useQueryStateFromUrl(hydrateDataQueryState);
  const updateQueryState = useUpdateQueryState(dehydrateDataQueryState);

  const page = Math.floor(queryState.paging.offset / ITEMS_PER_PAGE);
  const sort = queryState.query.sort;

  const lastRequestId = useRef<string>();
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const { data: currentUser } = useGetMeQuery();
  const [columnsButton, setColumnsButton] = useState<React.ReactNode>(null);

  const handleColumnsButtonRender = useCallback((button: React.ReactNode) => {
    setColumnsButton(button);
  }, []);

  const { starColumn, starredItems, starredOnly, toggleStarredOnly } =
    useTableStarring<Data>({
      resourceType: "data",
      cacheId: SHARED_BOOKMARK_CACHE_IDS.data,
    });

  const baseHandleRowClick = useRowNavigation<Data>(getDataDetailPath, {
    navigationState: { canGoBack: true },
  });
  const handleRowClick = useCallback(
    (row: Data, event?: React.MouseEvent<Element> | React.KeyboardEvent) => {
      tracker.event(DATA_EXPLORER_EVENTS.LIST_ROW_CLICKED);
      return baseHandleRowClick(row, event);
    },
    [baseHandleRowClick],
  );

  const navigate = useNavigate();

  const { action: editClassificationAction, dialog: editClassificationDialog } =
    useEditClassificationAction();

  const requestAccessDataAction: RowAction<Data> = useMemo(
    () => ({
      label: "Request access",
      getAction(row: Data) {
        return {
          onClick: () => {
            tracker.event(DATA_EXPLORER_EVENTS.LIST_REQUEST_ACCESS_CLICKED);
            navigate(getAccessRequestUrl(row.id));
          },
        };
      },
      icon: () => <VpnKeyOutlined fontSize="small" color="primary" />,
    }),
    [navigate],
  );

  const columns = useMemo(
    () =>
      [
        fields.id,
        fields.fullName,
        fields.creator,
        fields.account,
        fields.created,
        {
          ...fields.classification,
          contextActions: [editClassificationAction],
          contextActionsAlign: "right" as const,
        },
      ].map((column) => ({
        width: undefined,
        ...column,
        label: defaultFieldLabels[column.fieldName],
        defaultSortOrder: getDefaultSortOrder(column),
      })),
    [editClassificationAction],
  );

  const metadataColumns = useMemo(
    () =>
      columns.map((column) => {
        if (column.fieldName === "id") {
          return {
            ...column,
            contextActions: [requestAccessDataAction],
            contextActionsAlign: "right" as const,
          };
        }
        if (column.fieldName === "classification") {
          return { ...column, contextActions: undefined };
        }
        return column;
      }),
    [columns, requestAccessDataAction],
  );

  // Initialize URL params with default columns before DataTable renders
  useDataTableUrlInit({
    tableId: "data-explorer-home",
    enableColumnSelection: true,
    allColumns: columns,
  });

  const creatorFilterValue =
    queryState.query.filters.get("creator")?.valueList[0] ?? null;
  const idFilterValue = queryState.query.filters.get("id")?.valueList[0] ?? "";
  const fullNameFilterValue =
    queryState.query.filters.get(RESOURCE_NAME_FIELD.data)?.fuzzyMatch ?? "";
  const searchValue = fullNameFilterValue || idFilterValue;
  const createdRange = queryState.query.filters.get("created")?.range;
  const createdFilterValue =
    createdRange?.[0] && !isNaN(new Date(createdRange[0]).getTime())
      ? new Date(createdRange[0])
      : null;

  const {
    setFilter,
    handleCreatorChange: baseHandleCreatorChange,
    onSetQuickFilter: baseOnSetQuickFilter,
    quickFilterState,
  } = useFilters<Data>({
    queryState,
    updateQueryState,
    currentUserId: currentUser?.id,
  });

  const handleCreatorChange = useCallback(
    (value: string | null) => {
      tracker.event(DATA_EXPLORER_EVENTS.LIST_CREATOR_FILTER_CHANGED);
      baseHandleCreatorChange(value);
    },
    [baseHandleCreatorChange],
  );

  const { handleSortChange, handlePrevPage, handleNextPage } =
    useTableControls<Data>({
      queryState,
      updateQueryState,
    });

  const addSearchField = useCallback(
    (field: QueryFieldName, value: string | undefined) => {
      if (!value) return;
      if (field === "created" || field === "endDate") {
        tracker.event(DATA_EXPLORER_EVENTS.LIST_DATE_FILTER_CHANGED, field);
        const currentRange =
          queryState.query.filters.get("created")?.range ?? [];
        const newRange =
          field === "created"
            ? [value, currentRange[1] ?? value]
            : [currentRange[0] ?? value, value];
        setFilter("created", { range: newRange });
      } else {
        setFilter(field, { valueList: [value] });
      }
    },
    [queryState, setFilter],
  );

  const deleteSearchFields = useCallback(
    (fieldsToDelete: QueryFieldName[]) => {
      const newFilters = new Map(queryState.query.filters);
      fieldsToDelete.forEach((field) => {
        if (field === "endDate") {
          const currentRange = newFilters.get("created")?.range ?? [];
          if (currentRange.length > 0) {
            newFilters.set("created", {
              valueList: [],
              booleanValueList: [],
              fuzzyMatch: null,
              range: [currentRange[0]],
            });
          }
        } else if (field === "created") {
          newFilters.delete("created");
        } else {
          newFilters.delete(field as keyof Data);
        }
      });
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  const searchFieldsForApi = useMemo(() => {
    const result: { [key: string]: string } = {};
    for (const [
      fieldName,
      fieldFilters,
    ] of queryState.query.filters.entries()) {
      if (fieldFilters.valueList.length > 0) {
        result[fieldName] = fieldFilters.valueList[0] ?? "";
      } else if (fieldFilters.fuzzyMatch) {
        result[fieldName] = fieldFilters.fuzzyMatch;
      } else if (fieldFilters.range.length > 0) {
        result[fieldName] = fieldFilters.range[0] ?? "";
        if (fieldFilters.range[1]) {
          result["endDate"] = fieldFilters.range[1];
        }
      }
    }
    return result;
  }, [queryState.query.filters]);

  const [debouncedSearch] = useDebounce(searchValue, SEARCH_DEBOUNCE_MS, {
    leading: true,
  });

  // Only the search field which is typed (id or name) is debounced
  const debouncedSearchFieldsForApi = useMemo(() => {
    if (debouncedSearch === searchValue) return searchFieldsForApi;
    const next = { ...searchFieldsForApi };
    delete next.id;
    delete next[RESOURCE_NAME_FIELD.data];
    if (debouncedSearch) {
      if (queryState.query.filters.has("id")) {
        next.id = debouncedSearch;
      } else {
        next[RESOURCE_NAME_FIELD.data] = debouncedSearch;
      }
    }
    return next;
  }, [
    searchFieldsForApi,
    debouncedSearch,
    searchValue,
    queryState.query.filters,
  ]);

  useEffect(() => {
    if (ready && currentRequestId === null) {
      const requestId = Date.now().toString();
      lastRequestId.current = requestId;
      setCurrentRequestId(requestId);
      dispatch(
        fetchDataList({
          page,
          searchFields: debouncedSearchFieldsForApi,
          fieldSortedBy: sort.fieldName as FieldName,
          fieldSortDirection: sort.direction,
          permission: quickFilterState.isEditable ? "data:write" : "data:get",
        }),
      ).finally(() => {
        if (lastRequestId.current === requestId) {
          setCurrentRequestId(null);
        }
      });
    }
  }, [
    dispatch,
    page,
    ready,
    sort,
    debouncedSearchFieldsForApi,
    currentRequestId,
    quickFilterState.isEditable,
  ]);

  const handleSearchChange = useCallback(
    (filter: SearchFilter) => {
      tracker.event(DATA_EXPLORER_EVENTS.LIST_SEARCH_PERFORMED, filter.type);
      const newFilters = new Map(queryState.query.filters);
      newFilters.delete("id");
      newFilters.delete(RESOURCE_NAME_FIELD.data);

      if (filter.value) {
        if (filter.type === "id") {
          newFilters.set("id", {
            valueList: [filter.value],
            booleanValueList: [],
            fuzzyMatch: null,
            range: [],
          });
        } else {
          newFilters.set(RESOURCE_NAME_FIELD.data, {
            valueList: [],
            booleanValueList: [],
            fuzzyMatch: filter.value,
            range: [],
          });
        }
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  // Metadata fallback: when search returns no results, try the metadata endpoint.
  const hasSearchWithNoResults =
    !loading && list?.items?.length === 0 && !!debouncedSearch;
  const { data: metadataResult, isFetching: metadataFetching } =
    useGetResourceMetadataQuery(
      { identifier: debouncedSearch, type: "data" },
      { skip: !hasSearchWithNoResults },
    );

  const metadataItems: Data[] = useMemo(
    () =>
      metadataResult?.type === "data"
        ? [metadataResult as unknown as Data]
        : [],
    [metadataResult],
  );

  const showMetadataFallback =
    hasSearchWithNoResults &&
    !metadataFetching &&
    metadataItems.length > 0 &&
    debouncedSearch === searchValue;

  const isSearchDebouncePending =
    !!searchValue && debouncedSearch !== searchValue;

  useEffect(() => {
    if (showMetadataFallback) {
      tracker.event(DATA_EXPLORER_EVENTS.LIST_METADATA_FALLBACK_SHOWN);
    }
  }, [showMetadataFallback]);

  const { validationContextValue } = useValidationContextValue();

  const { show: showExtractHelp, dialog: extractHelpDialog } =
    useExtractTarHelp();
  const shownItems = useMemo(
    () => (starredOnly ? starredItems : (list?.items ?? [])),
    [starredOnly, starredItems, list?.items],
  );
  const hasEntries = useHasEntries(shownItems);

  const actions: RowAction<Data>[] = useMemo(
    () => [
      {
        label: "Download all (.tar)",
        getAction: (row: Data) => ({
          onClick: () => {
            tracker.event(DATA_EXPLORER_EVENTS.LIST_DOWNLOAD_CLICKED, row.id);
            startDownload(getFolderDownloadUrl(row.id, ""));
            showExtractHelp();
          },
        }),
        hidden: (row: Data) => !hasEntries[row.id],
        icon: () => <DownloadOutlined fontSize="small" color="primary" />,
      },
    ],
    [hasEntries, showExtractHelp],
  );
  // Metadata-fallback rows should not have a download button (user doesn't have full read permissions)
  const metadataActions: RowAction<Data>[] = useMemo(() => [], []);

  if (error) return <div>{error}</div>;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {editClassificationDialog}
      {extractHelpDialog}
      <ViewWrapper
        docsSection="DATA"
        title={DATA_EXPLORER_PAGE_TITLE}
        aboveContent={
          <>
            <ValidationContext.Provider value={validationContextValue}>
              <FilterBar>
                <UserRoleSelector
                  value={creatorFilterValue}
                  onChange={handleCreatorChange}
                />
                <DatePicker
                  addSearchField={addSearchField}
                  deleteSearchFields={deleteSearchFields}
                  selectedDate={createdFilterValue}
                />
              </FilterBar>
            </ValidationContext.Provider>
          </>
        }
      >
        <>
          <SearchRow>
            <NameIdSearch value={searchValue} onChange={handleSearchChange} />
          </SearchRow>
          <QuickFilters
            filters={QUICK_FILTERS}
            filterState={quickFilterState}
            setQuickFilter={(filter) => {
              tracker.event(
                DATA_EXPLORER_EVENTS.LIST_QUICK_FILTER_TOGGLED,
                filter.filterName,
              );
              baseOnSetQuickFilter({ filter });
            }}
            leadingChip={
              <StarredFilterChip
                active={starredOnly}
                onClick={toggleStarredOnly}
              />
            }
            columnsButton={columnsButton}
          />
          {starredOnly ? (
            starredItems.length === 0 ? (
              <NoneFoundMessage>
                You haven&apos;t starred any data yet. Hover a row and click the
                star to add one.
              </NoneFoundMessage>
            ) : (
              <DataTable
                ariaLabel="Starred data table"
                columns={columns}
                data={starredItems}
                fieldRenderers={fieldRenderers}
                sort={sort}
                paging={{
                  offset: 0,
                  limit: starredItems.length,
                  total: {
                    count: starredItems.length,
                    isCountFinal: true,
                  },
                }}
                setSort={() => {}}
                onPrevPageClick={() => {}}
                onNextPageClick={() => {}}
                onRowClick={handleRowClick}
                actions={actions}
                inlineActions
                starColumn={starColumn}
                enableColumnSelection={true}
                tableId="data-explorer-home"
                renderColumnsButtonExternally={true}
                onRenderColumnsButton={handleColumnsButtonRender}
              />
            )
          ) : (
            <>
              <LoadOverlay
                loading={loading || metadataFetching || isSearchDebouncePending}
              />
              {error && <p>ERROR: {error}</p>}
              {!loading &&
                !error &&
                list?.items?.length === 0 &&
                !showMetadataFallback &&
                !metadataFetching &&
                !isSearchDebouncePending && (
                  <NoneFoundMessage>
                    No data found for this query. Try adjusting your filters.
                  </NoneFoundMessage>
                )}
              {!error && showMetadataFallback && (
                <DataTable
                  ariaLabel="Data explorer table"
                  columns={metadataColumns}
                  data={metadataItems}
                  fieldRenderers={fieldRenderers}
                  sort={sort}
                  paging={{
                    offset: 0,
                    limit: ITEMS_PER_PAGE,
                    total: {
                      count: metadataItems.length,
                      isCountFinal: true,
                    },
                  }}
                  setSort={handleSortChange}
                  onPrevPageClick={handlePrevPage}
                  onNextPageClick={handleNextPage}
                  actions={metadataActions}
                  starColumn={starColumn}
                  onRowClick={() =>
                    tracker.event(
                      DATA_EXPLORER_EVENTS.LIST_METADATA_ROW_CLICKED,
                    )
                  }
                  onCellCopied={(fieldName) =>
                    tracker.event(
                      DATA_EXPLORER_EVENTS.LIST_CELL_COPIED,
                      fieldName,
                    )
                  }
                  enableColumnSelection={true}
                  tableId="data-explorer-home"
                  renderColumnsButtonExternally={true}
                  onRenderColumnsButton={handleColumnsButtonRender}
                  defaultSort={{
                    fieldName: defaultSortField.fieldName,
                    direction: getDefaultSortOrder(defaultSortField),
                  }}
                />
              )}
              {!error && list?.items && list.items.length > 0 && (
                <DataTable
                  ariaLabel="Data explorer table"
                  columns={columns}
                  data={list.items}
                  fieldRenderers={fieldRenderers}
                  sort={sort}
                  paging={{
                    offset: page * ITEMS_PER_PAGE,
                    limit: ITEMS_PER_PAGE,
                    total: {
                      count:
                        list?.metadata?.total ?? (page + 1) * ITEMS_PER_PAGE,
                      isCountFinal: true,
                    },
                  }}
                  setSort={handleSortChange}
                  onPrevPageClick={handlePrevPage}
                  onNextPageClick={handleNextPage}
                  onRowClick={handleRowClick}
                  onCellCopied={(fieldName) =>
                    tracker.event(
                      DATA_EXPLORER_EVENTS.LIST_CELL_COPIED,
                      fieldName,
                    )
                  }
                  actions={actions}
                  inlineActions
                  starColumn={starColumn}
                  enableColumnSelection={true}
                  tableId="data-explorer-home"
                  renderColumnsButtonExternally={true}
                  onRenderColumnsButton={handleColumnsButtonRender}
                  defaultSort={{
                    fieldName: defaultSortField.fieldName,
                    direction: getDefaultSortOrder(defaultSortField),
                  }}
                />
              )}
            </>
          )}
        </>
      </ViewWrapper>
    </LocalizationProvider>
  );
};

export default Home;
